import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

// Multimalt "Two Elixirs" — a duo of large side-by-side cards, one per
// variant (Mango + Apple, Date + B-vitamins). Sits between BrandHighlights
// and BrandLifestyle so the nursing-mother story still lands beneath it.
// -----------------------------------------------------------------------------
const SUN = "#F5D34A";
const TEAL = "#0E7B8A";
const INK = "#1E1A00";

const ELIXIRS = [
  {
    key: "mango",
    image: "/products/p15.webp",
    bg: "#F5D34A",
    ink: "#1E1A00",
    accent: "#0E7B8A",
    ribbon: "#4A7A3E",
  },
  {
    key: "date",
    image: "/products/p14.webp",
    bg: "#C4E4EC",
    ink: "#0F3541",
    accent: "#B0642F",
    ribbon: "#8A4325",
  },
];

const FALLBACK = {
  mango: {
    name: "The Bright Elixir.",
    flavour: "Mango · Apple · Malt · Botanicals",
    tag: "Everyday nourishment",
    body: "Barley malt with real apple and mango juice, then finished with fennel, fenugreek, goat's rue and blessed thistle — botanicals trusted by nursing mothers for over a century.",
    highlights: [
      "100% barley malt + real fruit juice",
      "Botanicals: fennel, fenugreek, goat's rue, blessed thistle",
      "No preservatives · no colourants · no added sugar",
    ],
  },
  date: {
    name: "The Rich Elixir.",
    flavour: "Date · Malt · Full B-vitamin Complex",
    tag: "Recovery & warmth",
    body: "Barley malt married to date-fruit juice — naturally rich in iron, potassium and magnesium. Fortified with a full B-vitamin complex (B1, B2, B3, B5, B6, Biotin, Folic Acid, B12).",
    highlights: [
      "Malt + date juice — natural iron & potassium",
      "8 essential B-vitamins including Folic Acid",
      "No preservatives · no colourants · no added sugar",
    ],
  },
};

function ElixirCard({ elixir, index }) {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const fb = FALLBACK[elixir.key];
  const nameRaw = t(`brandContent.multimalt.elixirs.${elixir.key}.name`);
  const flavourRaw = t(`brandContent.multimalt.elixirs.${elixir.key}.flavour`);
  const bodyRaw = t(`brandContent.multimalt.elixirs.${elixir.key}.body`);
  const tagRaw = t(`brandContent.multimalt.elixirs.${elixir.key}.tag`);
  const trHi = t(`brandContent.multimalt.elixirs.${elixir.key}.highlights`);
  const name = isTranslated(nameRaw) ? nameRaw : fb.name;
  const flavour = isTranslated(flavourRaw) ? flavourRaw : fb.flavour;
  const body = isTranslated(bodyRaw) ? bodyRaw : fb.body;
  const tag = isTranslated(tagRaw) ? tagRaw : fb.tag;
  const highlights = Array.isArray(trHi) && trHi.length === 3 ? trHi : fb.highlights;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.75, delay: index * 0.1 }}
      className="relative overflow-hidden rounded-3xl shadow-[0_25px_70px_-25px_rgba(30,26,0,0.35)] grid md:grid-cols-2"
      style={{ background: elixir.bg, color: elixir.ink }}
      data-testid={`multimalt-elixir-${elixir.key}`}
    >
      {/* Can column — oversized bottle takes over the left half */}
      <div className="relative flex items-end justify-center py-10 px-4 md:px-2 lg:px-4" style={{ minHeight: "460px" }}>
        <img
          src={elixir.image}
          alt={flavour}
          className="h-[440px] lg:h-[500px] w-auto object-contain drop-shadow-[0_25px_50px_rgba(0,0,0,0.22)]"
        />
      </div>

      {/* Copy column */}
      <div className="px-8 pb-9 pt-9 md:pt-12 flex flex-col">
        <span
          className="self-start inline-flex items-center gap-2 rounded-full px-3 py-1 text-[0.6rem] font-bold uppercase tracking-[0.28em]"
          style={{ background: elixir.ribbon, color: elixir.bg }}
        >
          {tag}
        </span>

        <h3
          className="mt-5 font-editorial italic leading-[1.05] text-3xl md:text-4xl"
          style={{ color: elixir.ink }}
        >
          {name}
        </h3>

        <p
          className="mt-2 text-[0.7rem] font-bold uppercase"
          style={{ color: elixir.accent, letterSpacing: "0.28em" }}
        >
          {flavour}
        </p>

        <div
          className="mt-4 h-px w-14"
          style={{ background: `linear-gradient(90deg, ${elixir.accent}, transparent)` }}
        />

        <p
          className="mt-4 text-sm md:text-[15px] leading-[1.7]"
          style={{ color: `${elixir.ink}dd` }}
        >
          {body}
        </p>

        <ul className="mt-5 space-y-2">
          {highlights.map((h, i) => (
            <li key={h} className="flex items-start gap-2 text-sm" style={{ color: `${elixir.ink}cc` }}>
              <span
                className="mt-[7px] w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: elixir.accent }}
              />
              <span>{h}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}

export default function MultimaltElixirs() {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const overlineRaw = t("brandContent.multimalt.elixirs.overline");
  const overline = isTranslated(overlineRaw) ? overlineRaw : "The Two Elixirs";
  const titleRaw = t("brandContent.multimalt.elixirs.title");
  const title = isTranslated(titleRaw)
    ? titleRaw
    : "Two recipes. One tradition of natural nourishment.";

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "#FCF6E1", color: INK }}
      data-testid="multimalt-elixirs"
    >
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
        <div className="max-w-2xl mb-14">
          <p
            className="text-[0.7rem] md:text-xs font-bold uppercase mb-4"
            style={{ color: TEAL, letterSpacing: "0.36em" }}
          >
            {overline}
          </p>
          <h2
            className="font-editorial italic leading-[1.05] text-[clamp(2.25rem,4.4vw,3.75rem)]"
            style={{ color: INK }}
          >
            {title}
          </h2>
          <div
            className="mt-6 h-px w-24"
            style={{ background: `linear-gradient(90deg, ${TEAL}, transparent)` }}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {ELIXIRS.map((e, i) => (
            <ElixirCard key={e.key} elixir={e} index={i} />
          ))}
        </div>
      </div>

      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${SUN} 20%, ${SUN} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
